import React, { useState, useEffect } from 'react';

const Industrial = () => {
  // Industrial solar solutions data
  const industrialSolutions = [
    {
      title: "Large-Scale Solar Plants",
      description: "Complete turnkey solutions for industrial-scale solar power plants from 1MW to 100MW+ capacity.",
      features: [
        "Land acquisition support",
        "EPC services",
        "Grid integration",
        "Operations & maintenance"
      ],
      icon: "🏭"
    },
    {
      title: "Rooftop Solar for Industries",
      description: "Maximize your rooftop space with high-efficiency solar systems designed for industrial buildings.",
      features: [
        "Structural assessment",
        "Custom design",
        "High-efficiency panels",
        "Fast installation"
      ],
      icon: "☀️"
    },
    {
      title: "Solar Water Pumping",
      description: "Reliable solar-powered water pumping solutions for industrial and agricultural applications.",
      features: [
        "AC/DC pumps",
        "Remote monitoring",
        "Automatic operation",
        "Low maintenance"
      ],
      icon: "💧"
    },
    {
      title: "Solar Thermal Solutions",
      description: "Industrial-grade solar thermal systems for process heating, steam generation, and cooling.",
      features: [
        "Process heat up to 300°C",
        "Steam generation",
        "Solar cooling",
        "Heat storage"
      ],
      icon: "🔥"
    },
    {
      title: "Energy Storage Systems",
      description: "Battery storage solutions for industrial backup power and load management.",
      features: [
        "Lithium-ion batteries",
        "Grid stabilization",
        "Peak shaving",
        "Backup power"
      ],
      icon: "🔋"
    },
    {
      title: "Hybrid Power Systems",
      description: "Integrated solar + diesel/gas hybrid systems for reliable 24/7 power supply.",
      features: [
        "Diesel generator integration",
        "Smart controllers",
        "Fuel optimization",
        "Remote monitoring"
      ],
      icon: "⚡"
    }
  ];

  // Case studies
  const caseStudies = [
    {
      company: "Textile Manufacturing Unit",
      location: "Nagpur, Maharashtra",
      capacity: "2.5 MW",
      savings: "₹1.2 Cr/year",
      details: "Rooftop solar installation on factory sheds"
    },
    {
      company: "Automotive Parts Manufacturer",
      location: "Amravati, Maharashtra",
      capacity: "5 MW",
      savings: "₹2.8 Cr/year",
      details: "Ground-mounted solar plant with storage"
    },
    {
      company: "Food Processing Plant",
      location: "Chandrapur, Maharashtra",
      capacity: "1.8 MW",
      savings: "₹95 Lakh/year",
      details: "Solar thermal for process heating"
    }
  ];

  // Benefits of industrial solar
  const benefits = [
    {
      title: "Reduced Electricity Costs",
      description: "Cut your electricity bills by 40-70% with solar power",
      icon: "💰"
    },
    {
      title: "Carbon Footprint Reduction",
      description: "Demonstrate environmental responsibility and sustainability",
      icon: "🌱"
    },
    {
      title: "Energy Independence",
      description: "Reduce dependency on grid power and diesel generators",
      icon: "🛡️"
    },
    {
      title: "Government Incentives",
      description: "Avail subsidies, accelerated depreciation, and tax benefits",
      icon: "📋"
    }
  ];

  // Process steps
  const processSteps = [
    {
      step: "1",
      title: "Feasibility Study",
      description: "Detailed site assessment and energy consumption analysis"
    },
    {
      step: "2",
      title: "Custom Design",
      description: "Engineering design optimized for your industrial needs"
    },
    {
      step: "3",
      title: "Financial Planning",
      description: "Capital expenditure, ROI analysis, and financing options"
    },
    {
      step: "4",
      title: "Installation",
      description: "Professional installation with minimal disruption"
    },
    {
      step: "5",
      title: "Commissioning",
      description: "Testing, grid synchronization, and performance validation"
    },
    {
      step: "6",
      title: "O&M Support",
      description: "24/7 monitoring and maintenance for 25+ years"
    }
  ];

  // Add chatbot states
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [awaitingLeadInfo, setAwaitingLeadInfo] = useState(false);
  const [leadStep, setLeadStep] = useState(0);
  const [userData, setUserData] = useState({ name: '', phone: '', city: '' });
  const [messageCount, setMessageCount] = useState(0);
  const [quickReplies, setQuickReplies] = useState([]);

  // Initialize chat when opened
  useEffect(() => {
    if (isChatOpen && chatMessages.length === 0) {
      initChat();
    }
  }, [isChatOpen]);

  // Chatbot functions
  const initChat = () => {
    addChatMessage("Hi 👋 Welcome to Blue Sky Energy! How can we help you with industrial solar today?", 'bot');
    setTimeout(() => {
      addChatMessage("Choose an option to get started:", 'bot');
      setQuickReplies([
        "🏭 Large-Scale Plants", 
        "🏢 Rooftop Solar", 
        "💰 ROI Analysis",
        "📋 Free Consultation"
      ]);
    }, 800);
  };

  const addChatMessage = (text, sender) => {
    const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const newMessage = {
      text,
      sender,
      time,
      id: Date.now()
    };
    setChatMessages(prev => [...prev, newMessage]);
    if (sender === 'user') {
      setMessageCount(prev => prev + 1);
    }
  };

  // Keyword Logic for Industrial
  const matchKeywords = (input) => {
    const text = input.toLowerCase();
    if (text.includes('human') || text.includes('call') || text.includes('talk')) {
      return "I'd be happy to connect you with our industrial team! 📞\n\nCall us directly: (+91) 80879 32436\nOr chat on WhatsApp:\n👉 https://wa.me/918087932436";
    }
    if (text.includes('large') || text.includes('plant') || text.includes('mw')) {
      return "🏭 Large-Scale Solar Plants\n\n• 1MW to 100MW+ capacity\n• Turnkey EPC solutions\n• Land acquisition support\n• Grid integration & approvals\n• 25+ years O&M support\n\nNeed a large-scale plant quote?";
    }
    if (text.includes('rooftop') || text.includes('factory') || text.includes('industrial')) {
      return "🏢 Industrial Rooftop Solar\n\n• Maximize rooftop space\n• High-efficiency panels\n• Structural assessment\n• Fast installation\n• Minimal disruption\n• 40-70% electricity cost savings\n\nWant rooftop solar details?";
    }
    if (text.includes('roi') || text.includes('return') || text.includes('investment')) {
      return "💰 Industrial Solar ROI\n\n• ROI period: 3-5 years\n• 40-70% electricity cost savings\n• Accelerated depreciation (80%)\n• Tax benefits available\n• 25+ years system lifespan\n\nWant a custom ROI analysis?";
    }
    if (text.includes('price') || text.includes('cost') || text.includes('quote')) {
      return "📊 Industrial Pricing\n\nTypical industrial installations:\n• 100kW: ₹45-55L\n• 500kW: ₹2-2.5Cr\n• 1MW: ₹4-5Cr\n• 5MW: ₹18-22Cr\n\nAll prices include installation, approvals, and maintenance. Want a custom quote?";
    }
    if (text.includes('thermal') || text.includes('heating') || text.includes('steam')) {
      return "🔥 Solar Thermal Solutions\n\n• Process heat up to 300°C\n• Steam generation\n• Solar cooling systems\n• Heat storage solutions\n• 30-50% fuel cost savings\n\nNeed industrial thermal solution?";
    }
    if (text.includes('storage') || text.includes('battery') || text.includes('backup')) {
      return "🔋 Energy Storage Systems\n\n• Lithium-ion batteries\n• Grid stabilization\n• Peak shaving capability\n• Backup power solutions\n• Load management\n\nInterested in storage solutions?";
    }
    if (text.includes('hybrid') || text.includes('diesel') || text.includes('generator')) {
      return "⚡ Hybrid Power Systems\n\n• Solar + diesel integration\n• 24/7 reliable power\n• Fuel optimization\n• Smart controllers\n• Remote monitoring\n• 30-50% diesel savings\n\nNeed hybrid system details?";
    }
    if (text.includes('subsidy') || text.includes('government') || text.includes('incentive')) {
      return "🏛️ Government Incentives\n\n• Accelerated depreciation (80%)\n• Capital subsidies available\n• State government schemes\n• Tax benefits\n• Green energy certificates\n\nNeed incentive details?";
    }
    return "I understand you're interested in industrial solar! I can help with large-scale plants, rooftop solar, ROI analysis, pricing, or connecting you with our industrial team.";
  };

  // Lead Capture Logic
  const handleLeadCapture = (input) => {
    if (leadStep === 0) {
      setUserData(prev => ({ ...prev, name: input }));
      setLeadStep(1);
      addChatMessage(`Nice to meet you, ${input}! 😊\n\nCould you please share your phone number?`, 'bot');
    } else if (leadStep === 1) {
      setUserData(prev => ({ ...prev, phone: input }));
      setLeadStep(2);
      addChatMessage("Great! And which city are you from?", 'bot');
    } else if (leadStep === 2) {
      setUserData(prev => ({ ...prev, city: input }));
      setLeadCaptured(true);
      setAwaitingLeadInfo(false);
      saveLead({ ...userData, city: input });
      addChatMessage(`Perfect! Thank you ${userData.name}! ✅\n\nOur industrial solar expert will contact you within 24 hours with a comprehensive proposal.`, 'bot');
    }
  };

  const saveLead = (data) => {
    const leadId = `lead_${Date.now()}`;
    localStorage.setItem(leadId, JSON.stringify({...data, timestamp: new Date()}));
    console.log("Industrial Lead Saved:", data);
  };

  const handleChatSend = () => {
    const text = chatInput.trim();
    if (!text) return;

    addChatMessage(text, 'user');
    setChatInput('');
    setQuickReplies([]);

    setTimeout(() => {
      if (awaitingLeadInfo) {
        handleLeadCapture(text);
      } else {
        const response = matchKeywords(text);
        addChatMessage(response, 'bot');

        // Trigger Lead Capture after 2 interactions
        if (!leadCaptured && messageCount >= 1) {
          setTimeout(() => {
            addChatMessage("I'd love to help you with industrial solar! 📋\n\nCan I get your name to assist you better?", 'bot');
            setAwaitingLeadInfo(true);
          }, 1500);
        }
      }
    }, 600);
  };

  const handleQuickReply = (option) => {
    setChatInput(option);
    setTimeout(() => {
      handleChatSend();
    }, 100);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleChatSend();
    }
  };

  // Internal CSS with ONLY COLOR CHANGES
  const styles = {
    container: {
      fontFamily: "Cambria, serif",
      color: "#123249",
      backgroundColor: "#ffffff",
      position: "relative",
      overflowX: "hidden",
      width: "100%"
    },
    
    // Chatbot Styles - Blue Theme
    chatbotContainer: {
      position: "fixed",
      bottom: "20px",
      right: "20px",
      zIndex: "9999",
      fontFamily: "Cambria, serif"
    },
    chatToggleBtn: {
      position: "fixed",
      bottom: "20px",
      right: "20px",
      width: "60px",
      height: "60px",
      borderRadius: "50%",
      background: "linear-gradient(135deg, #447793, #2D5A74)",
      color: "white",
      border: "2px solid #123249",
      fontSize: "24px",
      cursor: "pointer",
      boxShadow: "0 8px 30px rgba(68, 119, 147, 0.3)",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: "10000",
      padding: "0",
      animation: "pulse 2s infinite"
    },
    chatWindow: {
      position: "fixed",
      bottom: "90px",
      right: "20px",
      width: "380px",
      maxWidth: "calc(100vw - 40px)",
      height: "550px",
      maxHeight: "70vh",
      background: "white",
      borderRadius: "16px",
      boxShadow: "0 10px 40px rgba(6, 18, 33, 0.15)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      zIndex: "9999",
      transition: "all 0.3s ease",
      opacity: isChatOpen ? "1" : "0",
      visibility: isChatOpen ? "visible" : "hidden",
      transform: isChatOpen ? "translateY(0)" : "translateY(20px)",
      border: "1px solid #DDE8ED"
    },
    chatHeader: {
      background: "linear-gradient(135deg, #447793, #2D5A74)",
      color: "white",
      padding: "16px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
    },
    headerInfo: {
      display: "flex",
      alignItems: "center",
      gap: "12px"
    },
    headerIcon: {
      background: "white",
      padding: "10px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "40px",
      height: "40px",
      fontSize: "18px",
      border: "2px solid #123249"
    },
    chatHeaderTitle: {
      margin: "0",
      fontSize: "16px",
      fontWeight: "600",
      lineHeight: "1.2",
      color: "white"
    },
    chatHeaderSubtitle: {
      margin: "0",
      fontSize: "12px",
      opacity: "0.9",
      lineHeight: "1.2",
      color: "#DDE8ED"
    },
    closeChatBtn: {
      background: "none",
      border: "none",
      color: "white",
      cursor: "pointer",
      fontSize: "18px",
      padding: "5px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "opacity 0.2s"
    },
    quickReplies: {
      padding: "12px",
      display: "flex",
      flexWrap: "wrap",
      gap: "8px",
      background: "#DDE8ED",
      borderBottom: "1px solid #DDE8ED"
    },
    replyBtn: {
      background: "white",
      border: "1px solid #447793",
      color: "#123249",
      padding: "8px 16px",
      borderRadius: "20px",
      fontSize: "13px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      whiteSpace: "nowrap",
      fontWeight: "500",
      fontFamily: "Cambria, serif"
    },
    chatMessages: {
      flex: "1",
      padding: "16px",
      background: "#DDE8ED",
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
      gap: "12px"
    },
    message: {
      maxWidth: "80%",
      padding: "12px 16px",
      borderRadius: "18px",
      fontSize: "14px",
      lineHeight: "1.5",
      whiteSpace: "pre-line",
      wordWrap: "break-word",
      position: "relative",
      animation: "fadeIn 0.3s ease"
    },
    botMessage: {
      background: "white",
      alignSelf: "flex-start",
      borderBottomLeftRadius: "4px",
      boxShadow: "0 2px 8px rgba(6, 18, 33, 0.08)",
      color: "#123249",
      border: "1px solid #DDE8ED"
    },
    userMessage: {
      background: "linear-gradient(135deg, #447793, #2D5A74)",
      color: "white",
      alignSelf: "flex-end",
      borderBottomRightRadius: "4px"
    },
    messageTime: {
      fontSize: "11px",
      marginTop: "6px",
      display: "block",
      opacity: "0.7"
    },
    chatInputArea: {
      padding: "16px",
      borderTop: "1px solid #DDE8ED",
      background: "white"
    },
    inputWrapper: {
      display: "flex",
      gap: "10px",
      marginBottom: "8px"
    },
    chatInput: {
      flex: "1",
      padding: "12px 16px",
      border: "1px solid #DDE8ED",
      borderRadius: "25px",
      outline: "none",
      fontSize: "14px",
      fontFamily: "Cambria, serif",
      transition: "border-color 0.2s",
      color: "#123249"
    },
    sendBtn: {
      background: "linear-gradient(135deg, #447793, #2D5A74)",
      color: "white",
      border: "2px solid #123249",
      width: "45px",
      height: "45px",
      borderRadius: "50%",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "16px",
      transition: "all 0.2s ease",
      padding: "0"
    },
    footerText: {
      textAlign: "center",
      fontSize: "11px",
      color: "#2D5A74",
      margin: "0"
    },
    
    // Hero Section with Background Image - Blue Theme
    heroSection: {
      background: `linear-gradient(rgba(18, 50, 73, 0.9), rgba(45, 90, 116, 0.9)), url('https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      color: "white",
      padding: "60px 5%",
      textAlign: "center",
      position: "relative",
      overflow: "hidden",
      width: "100%",
      minHeight: "600px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center"
    },
    heroOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "linear-gradient(135deg, rgba(18, 50, 73, 0.9) 0%, rgba(45, 90, 116, 0.85) 100%)",
      zIndex: 1
    },
    heroContent: {
      position: "relative",
      zIndex: 2,
      maxWidth: "1200px",
      width: "100%"
    },
    heroTitle: {
      fontSize: "2.5rem",
      fontWeight: "800",
      marginBottom: "15px",
      textShadow: "2px 2px 4px rgba(6, 18, 33, 0.3)",
      lineHeight: "1.2",
      color: "white"
    },
    heroSubtitle: {
      fontSize: "1.2rem",
      marginBottom: "30px",
      opacity: "0.95",
      maxWidth: "800px",
      margin: "0 auto",
      lineHeight: "1.6",
      fontWeight: "400",
      color: "#DDE8ED"
    },
    heroStats: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: "20px",
      marginTop: "40px",
      maxWidth: "800px",
      margin: "0 auto"
    },
    statBox: {
      textAlign: "center",
      padding: "20px 15px",
      backgroundColor: "rgba(68, 119, 147, 0.15)",
      borderRadius: "12px",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(221, 232, 237, 0.2)",
      transition: "all 0.3s ease"
    },
    statValue: {
      fontSize: "1.8rem",
      fontWeight: "700",
      color: "#DDE8ED",
      marginBottom: "5px",
      textShadow: "1px 1px 2px rgba(6, 18, 33, 0.2)"
    },
    statLabel: {
      fontSize: "0.9rem",
      opacity: "0.95",
      fontWeight: "500",
      color: "white"
    },
    
    // Section Styles - Blue Theme
    section: {
      padding: "50px 5%",
      maxWidth: "1200px",
      margin: "0 auto",
      width: "100%",
      boxSizing: "border-box"
    },
    sectionTitle: {
      fontSize: "2rem",
      fontWeight: "700",
      color: "#123249",
      textAlign: "center",
      marginBottom: "15px",
      position: "relative",
      lineHeight: "1.3"
    },
    sectionTitleAfter: {
      width: "80px",
      height: "4px",
      backgroundColor: "#447793",
      margin: "15px auto 30px",
      borderRadius: "2px"
    },
    sectionSubtitle: {
      fontSize: "1rem",
      color: "#2D5A74",
      textAlign: "center",
      marginBottom: "40px",
      maxWidth: "800px",
      margin: "0 auto",
      lineHeight: "1.6"
    },
    
    // Solutions Grid - Blue Theme
    solutionsGrid: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: "25px",
      marginTop: "40px"
    },
    solutionCard: {
      backgroundColor: "white",
      borderRadius: "12px",
      padding: "25px",
      boxShadow: "0 5px 15px rgba(6, 18, 33, 0.08)",
      transition: "all 0.3s ease",
      cursor: "pointer",
      border: "2px solid transparent",
      boxSizing: "border-box"
    },
    solutionCardHover: {
      transform: "translateY(-5px)",
      boxShadow: "0 10px 25px rgba(68, 119, 147, 0.15)",
      borderColor: "#447793"
    },
    solutionIcon: {
      fontSize: "2.5rem",
      marginBottom: "15px",
      width: "80px",
      height: "80px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "2px solid #123249",
      borderRadius: "50%",
      margin: "0 auto 15px",
      background: "white"
    },
    solutionTitle: {
      fontSize: "1.3rem",
      fontWeight: "600",
      color: "#123249",
      marginBottom: "12px",
      lineHeight: "1.3",
      textAlign: "center"
    },
    solutionDescription: {
      color: "#2D5A74",
      lineHeight: "1.6",
      marginBottom: "15px",
      fontSize: "0.95rem",
      textAlign: "center"
    },
    solutionFeatures: {
      listStyle: "none",
      padding: "0",
      margin: "0"
    },
    solutionFeature: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      marginBottom: "8px",
      color: "#2D5A74",
      fontSize: "0.9rem"
    },
    featureIcon: {
      color: "#447793",
      fontSize: "1.1rem"
    },
    
    // Benefits Section - Blue Theme
    benefitsSection: {
      backgroundColor: "#DDE8ED"
    },
    benefitsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: "20px",
      marginTop: "40px"
    },
    benefitCard: {
      backgroundColor: "white",
      borderRadius: "10px",
      padding: "25px",
      textAlign: "center",
      boxShadow: "0 4px 15px rgba(6, 18, 33, 0.05)",
      transition: "all 0.3s ease",
      border: "2px solid transparent"
    },
    benefitCardHover: {
      transform: "translateY(-3px)",
      boxShadow: "0 8px 20px rgba(68, 119, 147, 0.1)",
      borderColor: "#447793"
    },
    benefitIcon: {
      fontSize: "2rem",
      marginBottom: "15px",
      width: "70px",
      height: "70px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "2px solid #123249",
      borderRadius: "50%",
      margin: "0 auto 15px",
      background: "white"
    },
    benefitTitle: {
      fontSize: "1.1rem",
      fontWeight: "600",
      color: "#123249",
      marginBottom: "10px"
    },
    
    // Process Steps - Blue Theme
    processStepsContainer: {
      display: "flex",
      flexDirection: "column",
      gap: "25px",
      padding: "20px 0",
      marginTop: "40px"
    },
    processStep: {
      backgroundColor: "white",
      borderRadius: "12px",
      padding: "25px",
      boxShadow: "0 5px 15px rgba(6, 18, 33, 0.05)",
      position: "relative",
      boxSizing: "border-box",
      border: "2px solid transparent"
    },
    processStepNumber: {
      position: "absolute",
      top: "-15px",
      left: "15px",
      backgroundColor: "#447793",
      color: "white",
      width: "35px",
      height: "35px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: "bold",
      fontSize: "1rem"
    },
    processStepTitle: {
      fontSize: "1.2rem",
      fontWeight: "600",
      color: "#123249",
      marginBottom: "12px",
      marginTop: "10px",
      lineHeight: "1.3"
    },
    
    // Case Studies - Blue Theme
    caseStudiesGrid: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: "25px",
      marginTop: "40px"
    },
    caseStudyCard: {
      backgroundColor: "white",
      borderRadius: "10px",
      padding: "20px",
      borderLeft: "5px solid #447793",
      boxShadow: "0 4px 15px rgba(6, 18, 33, 0.05)",
      boxSizing: "border-box"
    },
    caseStudyCompany: {
      fontSize: "1.2rem",
      fontWeight: "600",
      color: "#123249",
      marginBottom: "10px",
      lineHeight: "1.3"
    },
    caseStudyDetail: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: "8px",
      paddingBottom: "8px",
      borderBottom: "1px solid #DDE8ED",
      fontSize: "0.9rem"
    },
    caseStudyLabel: {
      fontWeight: "600",
      color: "#2D5A74"
    },
    caseStudyValue: {
      color: "#447793",
      fontWeight: "600"
    },
    
    // CTA Section - Blue Theme
    ctaSection: {
      backgroundColor: "#123249",
      color: "white",
      padding: "50px 5%",
      textAlign: "center",
      borderRadius: "12px",
      margin: "50px auto",
      maxWidth: "1000px",
      width: "90%",
      boxSizing: "border-box",
      border: "2px solid #447793"
    },
    ctaTitle: {
      fontSize: "1.8rem",
      fontWeight: "700",
      marginBottom: "15px",
      lineHeight: "1.3",
      color: "white"
    },
    ctaSubtitle: {
      fontSize: "1rem",
      opacity: "0.9",
      marginBottom: "30px",
      maxWidth: "800px",
      margin: "0 auto",
      lineHeight: "1.6",
      color: "#DDE8ED"
    },
    ctaButton: {
      backgroundColor: "#447793",
      color: "white",
      padding: "12px 35px",
      fontSize: "1.1rem",
      fontWeight: "bold",
      border: "2px solid #123249",
      borderRadius: "50px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      boxShadow: "0 4px 15px rgba(6, 18, 33, 0.2)",
      width: "100%",
      maxWidth: "300px"
    },
    ctaButtonHover: {
      backgroundColor: "#2D5A74",
      transform: "scale(1.03)",
      boxShadow: "0 6px 20px rgba(6, 18, 33, 0.3)"
    },
    
    // Contact Form - Blue Theme
    contactForm: {
      backgroundColor: "white",
      padding: "30px",
      borderRadius: "12px",
      boxShadow: "0 10px 30px rgba(6, 18, 33, 0.1)",
      maxWidth: "800px",
      margin: "0 auto",
      boxSizing: "border-box",
      width: "100%",
      border: "2px solid #447793"
    },
    formTitle: {
      fontSize: "1.5rem",
      fontWeight: "600",
      color: "#123249",
      marginBottom: "25px",
      textAlign: "center",
      lineHeight: "1.3"
    },
    formGroup: {
      marginBottom: "20px",
      width: "100%"
    },
    formLabel: {
      display: "block",
      marginBottom: "8px",
      fontWeight: "600",
      color: "#2D5A74",
      fontSize: "0.95rem"
    },
    formInput: {
      width: "100%",
      padding: "12px 15px",
      border: "2px solid #DDE8ED",
      borderRadius: "8px",
      fontSize: "1rem",
      transition: "all 0.3s ease",
      boxSizing: "border-box",
      color: "#123249"
    },
    formInputFocus: {
      borderColor: "#447793",
      outline: "none"
    },
    formTextarea: {
      width: "100%",
      padding: "12px 15px",
      border: "2px solid #DDE8ED",
      borderRadius: "8px",
      fontSize: "1rem",
      minHeight: "120px",
      resize: "vertical",
      boxSizing: "border-box",
      fontFamily: "Cambria, serif",
      color: "#123249"
    },
    submitButton: {
      backgroundColor: "#447793",
      color: "white",
      padding: "15px 40px",
      fontSize: "1.1rem",
      fontWeight: "600",
      border: "2px solid #123249",
      borderRadius: "8px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      width: "100%",
      boxSizing: "border-box"
    },
    submitButtonHover: {
      backgroundColor: "#2D5A74",
      transform: "translateY(-2px)"
    }
  };

  // State for hover effects
  const [hoverStates, setHoverStates] = useState({
    solutionCards: Array(industrialSolutions.length).fill(false),
    benefitCards: Array(benefits.length).fill(false),
    ctaButton: false,
    submitButton: false,
    chatbotButton: false,
    statBoxes: Array(4).fill(false)
  });

  const handleMouseEnter = (type, index) => {
    setHoverStates(prev => ({
      ...prev,
      [type]: type === 'ctaButton' || type === 'submitButton' || type === 'chatbotButton'
        ? true 
        : prev[type].map((item, i) => i === index ? true : item)
    }));
  };

  const handleMouseLeave = (type, index) => {
    setHoverStates(prev => ({
      ...prev,
      [type]: type === 'ctaButton' || type === 'submitButton' || type === 'chatbotButton'
        ? false 
        : prev[type].map((item, i) => i === index ? false : item)
    }));
  };

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    capacity: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.company || !formData.email || !formData.phone || !formData.capacity) {
      alert('Please fill all required fields: Name, Company, Email, Phone, and Capacity');
      return;
    }

    try {
      console.log('Submitting industrial form:', formData);
      
      // Send data to server
      const response = await fetch('http://localhost:5000/api/industrial', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          company: formData.company,
          email: formData.email,
          phone: formData.phone,
          capacity: formData.capacity,
          message: formData.message || ''
        }),
      });

      const result = await response.json();
      console.log('Server response:', result);

      if (result.success) {
        alert('✅ Thank you for your inquiry! We will contact you shortly with a detailed proposal.');
        
        // Reset form
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          capacity: '',
          message: ''
        });
      } else {
        alert(`❌ ${result.error || 'Error submitting form. Please try again or contact us directly.'}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('❌ Network error. Please check your connection and try again.');
    }
  };

  // Responsive CSS Media Queries with ONLY COLOR CHANGES
  const responsiveCSS = `
    /* Add Font Awesome for icons */
    @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');
    
    /* Icon Container Styling - Blue Theme */
    .solution-icon,
    .benefit-icon,
    .header-icon {
      background: white !important;
      border: 2px solid #123249 !important;
      color: #123249 !important;
    }
    
    .solution-icon i,
    .benefit-icon i,
    .header-icon i {
      color: #123249 !important;
    }
    
    /* Chat button and send button borders */
    .chat-toggle-btn,
    .send-btn,
    .cta-button,
    .submit-button {
      border: 2px solid #123249 !important;
    }
    
    /* Responsive CSS for Industrial Page */
    @media (max-width: 768px) {
      .hero-section {
        padding: 40px 5% !important;
        min-height: 500px !important;
      }
      
      .hero-title {
        font-size: 2rem !important;
      }
      
      .hero-subtitle {
        font-size: 1rem !important;
      }
      
      .section {
        padding: 40px 5% !important;
      }
      
      .section-title {
        font-size: 1.6rem !important;
      }
      
      .hero-stats {
        grid-template-columns: repeat(2, 1fr) !important;
        gap: 15px !important;
        margin-top: 30px !important;
      }
      
      .stat-box {
        padding: 15px !important;
      }
      
      .stat-value {
        font-size: 1.5rem !important;
      }
      
      .stat-label {
        font-size: 0.8rem !important;
      }
      
      .solutions-grid {
        grid-template-columns: 1fr !important;
        gap: 20px !important;
      }
      
      .solution-card {
        padding: 20px !important;
      }
      
      .solution-title {
        font-size: 1.2rem !important;
      }
      
      .solution-icon {
        width: 70px !important;
        height: 70px !important;
        font-size: 2rem !important;
      }
      
      .benefits-grid {
        grid-template-columns: repeat(2, 1fr) !important;
        gap: 15px !important;
      }
      
      .benefit-card {
        padding: 20px !important;
      }
      
      .benefit-icon {
        width: 60px !important;
        height: 60px !important;
        font-size: 1.8rem !important;
      }
      
      .case-studies-grid {
        grid-template-columns: 1fr !important;
      }
      
      .case-study-card {
        padding: 18px !important;
      }
      
      .cta-section {
        padding: 40px 5% !important;
        margin: 40px auto !important;
        width: 95% !important;
      }
      
      .cta-title {
        font-size: 1.5rem !important;
      }
      
      .cta-subtitle {
        font-size: 0.95rem !important;
      }
      
      .cta-button {
        padding: 12px 25px !important;
        font-size: 1rem !important;
      }
      
      .contact-form {
        padding: 25px !important;
      }
      
      .form-title {
        font-size: 1.3rem !important;
      }
      
      .form-input, .form-textarea {
        padding: 10px 12px !important;
        font-size: 0.95rem !important;
      }
      
      .chat-window {
        width: calc(100vw - 40px) !important;
        height: 500px !important;
        bottom: 80px !important;
        right: 20px !important;
        left: 20px !important;
        max-height: 65vh !important;
      }
      
      .chat-toggle-btn {
        width: 55px !important;
        height: 55px !important;
        font-size: 22px !important;
        bottom: 20px !important;
        right: 20px !important;
      }
      
      .chat-header {
        padding: 14px !important;
      }
      
      .chat-header-title {
        font-size: 14px !important;
      }
      
      .chat-messages {
        padding: 14px !important;
      }
      
      .message {
        max-width: 85% !important;
        font-size: 13px !important;
        padding: 10px 14px !important;
      }
      
      .reply-btn {
        font-size: 12px !important;
        padding: 6px 12px !important;
        white-space: normal !important;
      }
      
      .quick-replies {
        padding: 10px !important;
        gap: 6px !important;
      }
    }
    
    @media (max-width: 480px) {
      .hero-section {
        min-height: 450px !important;
      }
      
      .hero-title {
        font-size: 1.8rem !important;
      }
      
      .hero-subtitle {
        font-size: 0.9rem !important;
      }
      
      .hero-stats {
        grid-template-columns: 1fr !important;
      }
      
      .section-title {
        font-size: 1.4rem !important;
      }
      
      .section-subtitle {
        font-size: 0.9rem !important;
      }
      
      .benefits-grid {
        grid-template-columns: 1fr !important;
      }
      
      .benefit-card {
        padding: 18px !important;
      }
      
      .process-step {
        padding: 20px !important;
      }
      
      .process-step-title {
        font-size: 1.1rem !important;
      }
      
      .cta-section {
        padding: 30px 5% !important;
      }
      
      .cta-title {
        font-size: 1.3rem !important;
      }
      
      .cta-subtitle {
        font-size: 0.9rem !important;
      }
      
      .chat-window {
        width: calc(100vw - 20px) !important;
        right: 10px !important;
        left: 10px !important;
        height: 450px !important;
      }
      
      .chat-toggle-btn {
        width: 50px !important;
        height: 50px !important;
        font-size: 20px !important;
        bottom: 15px !important;
        right: 15px !important;
      }
      
      .message {
        max-width: 90% !important;
        font-size: 12px !important;
        padding: 8px 12px !important;
      }
      
      .quick-replies {
        flex-wrap: wrap !important;
      }
      
      .reply-btn {
        flex: 1 !important;
        min-width: calc(50% - 6px) !important;
        font-size: 11px !important;
        padding: 5px 8px !important;
      }
    }
    
    @media (min-width: 769px) {
      .solutions-grid {
        grid-template-columns: repeat(2, 1fr) !important;
      }
      
      .case-studies-grid {
        grid-template-columns: repeat(3, 1fr) !important;
      }
      
      .process-steps-container {
        flex-direction: row !important;
        flex-wrap: wrap !important;
        justify-content: center !important;
      }
      
      .process-step {
        flex: 0 0 calc(33.333% - 20px) !important;
        max-width: 300px !important;
      }
    }
    
    @media (min-width: 1024px) {
      .hero-section {
        padding: 80px 5% !important;
        min-height: 700px !important;
      }
      
      .hero-title {
        font-size: 3rem !important;
      }
      
      .hero-stats {
        grid-template-columns: repeat(4, 1fr) !important;
        gap: 30px !important;
      }
      
      .solutions-grid {
        grid-template-columns: repeat(3, 1fr) !important;
      }
      
      .benefits-grid {
        grid-template-columns: repeat(4, 1fr) !important;
      }
      
      .process-steps-container {
        display: grid !important;
        grid-template-columns: repeat(3, 1fr) !important;
        gap: 30px !important;
      }
      
      .process-step {
        flex: none !important;
        max-width: 100% !important;
      }
      
      .section {
        padding: 70px 5% !important;
      }
      
      .section-title {
        font-size: 2.5rem !important;
      }
    }
    
    @media (min-width: 1200px) {
      .solutions-grid {
        grid-template-columns: repeat(3, 1fr) !important;
        gap: 30px !important;
      }
      
      .benefits-grid {
        grid-template-columns: repeat(4, 1fr) !important;
        gap: 25px !important;
      }
      
      .case-studies-grid {
        grid-template-columns: repeat(3, 1fr) !important;
        gap: 30px !important;
      }
    }
    
    /* Animation fixes for mobile */
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(5px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes pulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.05); }
      100% { transform: scale(1); }
    }
    
    /* Stat box hover effect */
    .stat-box:hover {
      transform: translateY(-5px);
      background-color: rgba(68, 119, 147, 0.25) !important;
      box-shadow: 0 10px 25px rgba(6, 18, 33, 0.15) !important;
    }
    
    /* Input focus states */
    .form-input:focus,
    .form-textarea:focus {
      border-color: #447793 !important;
      outline: none !important;
    }
  `;

  return (
    <div style={styles.container}>
      {/* Responsive CSS Styles */}
      <style>{responsiveCSS}</style>

      {/* Hero Section with Background Image - Blue Theme */}
      <section style={styles.heroSection} className="hero-section">
        <div style={styles.heroOverlay}></div>
        <div style={styles.heroContent} className="hero-content">
          <h1 style={styles.heroTitle} className="hero-title">Industrial Solar Solutions</h1>
          <p style={styles.heroSubtitle} className="hero-subtitle">
            Power your industry with reliable, cost-effective Blue Sky energy solutions. 
            Reduce operational costs and boost sustainability with our customized solar installations.
          </p>
          
          <div style={styles.heroStats} className="hero-stats">
            <div 
              style={styles.statBox} 
              className="stat-box"
              onMouseEnter={() => handleMouseEnter('statBoxes', 0)}
              onMouseLeave={() => handleMouseLeave('statBoxes', 0)}
            >
              <div style={styles.statValue} className="stat-value">40-70%</div>
              <div style={styles.statLabel} className="stat-label">Electricity Cost Savings</div>
            </div>
            <div 
              style={styles.statBox} 
              className="stat-box"
              onMouseEnter={() => handleMouseEnter('statBoxes', 1)}
              onMouseLeave={() => handleMouseLeave('statBoxes', 1)}
            >
              <div style={styles.statValue} className="stat-value">3-5 Years</div>
              <div style={styles.statLabel} className="stat-label">ROI Period</div>
            </div>
            <div 
              style={styles.statBox} 
              className="stat-box"
              onMouseEnter={() => handleMouseEnter('statBoxes', 2)}
              onMouseLeave={() => handleMouseLeave('statBoxes', 2)}
            >
              <div style={styles.statValue} className="stat-value">25+ Years</div>
              <div style={styles.statLabel} className="stat-label">System Lifespan</div>
            </div>
            <div 
              style={styles.statBox} 
              className="stat-box"
              onMouseEnter={() => handleMouseEnter('statBoxes', 3)}
              onMouseLeave={() => handleMouseLeave('statBoxes', 3)}
            >
              <div style={styles.statValue} className="stat-value">100+</div>
              <div style={styles.statLabel} className="stat-label">Industrial Projects</div>
            </div>
          </div>
        </div>
      </section>

      {/* Industrial Solutions - Blue Theme */}
      <section style={styles.section} className="section">
        <h2 style={styles.sectionTitle} className="section-title">Our Industrial Solar Solutions</h2>
        <div style={styles.sectionTitleAfter} className="section-title-after"></div>
        <p style={styles.sectionSubtitle} className="section-subtitle">
          Comprehensive solar energy solutions designed specifically for industrial and commercial applications
        </p>

        <div style={styles.solutionsGrid} className="solutions-grid">
          {industrialSolutions.map((solution, index) => (
            <div 
              key={index}
              style={{
                ...styles.solutionCard,
                ...(hoverStates.solutionCards[index] ? styles.solutionCardHover : {})
              }}
              className="solution-card"
              onMouseEnter={() => handleMouseEnter('solutionCards', index)}
              onMouseLeave={() => handleMouseLeave('solutionCards', index)}
            >
              <div style={styles.solutionIcon} className="solution-icon">
                <i className={`fas ${index === 0 ? 'fa-industry' : index === 1 ? 'fa-sun' : index === 2 ? 'fa-faucet-drip' : index === 3 ? 'fa-fire' : index === 4 ? 'fa-battery-full' : 'fa-bolt'}`}></i>
              </div>
              <h3 style={styles.solutionTitle} className="solution-title">{solution.title}</h3>
              <p style={styles.solutionDescription} className="solution-description">{solution.description}</p>
              <ul style={styles.solutionFeatures} className="solution-features">
                {solution.features.map((feature, featureIndex) => (
                  <li key={featureIndex} style={styles.solutionFeature} className="solution-feature">
                    <span style={styles.featureIcon} className="feature-icon">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits Section - Blue Theme */}
      <section style={{...styles.section, ...styles.benefitsSection}} className="section benefits-section">
        <h2 style={styles.sectionTitle} className="section-title">Benefits for Industries</h2>
        <div style={styles.sectionTitleAfter} className="section-title-after"></div>
        
        <div style={styles.benefitsGrid} className="benefits-grid">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              style={{
                ...styles.benefitCard,
                ...(hoverStates.benefitCards[index] ? styles.benefitCardHover : {})
              }}
              className="benefit-card"
              onMouseEnter={() => handleMouseEnter('benefitCards', index)}
              onMouseLeave={() => handleMouseLeave('benefitCards', index)}
            >
              <div style={styles.benefitIcon} className="benefit-icon">
                <i className={`fas ${index === 0 ? 'fa-money-bill-wave' : index === 1 ? 'fa-leaf' : index === 2 ? 'fa-shield-alt' : 'fa-file-alt'}`}></i>
              </div>
              <h3 style={styles.benefitTitle} className="benefit-title">{benefit.title}</h3>
              <p style={{color: "#2D5A74", fontSize: "0.9rem", lineHeight: "1.5"}}>{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Implementation Process - Blue Theme */}
      <section style={styles.section} className="section">
        <h2 style={styles.sectionTitle} className="section-title">Our Implementation Process</h2>
        <div style={styles.sectionTitleAfter} className="section-title-after"></div>
        <p style={styles.sectionSubtitle} className="section-subtitle">
          A streamlined process designed for industrial projects with minimal disruption
        </p>

        <div style={styles.processStepsContainer} className="process-steps-container">
          {processSteps.map((step, index) => (
            <div key={index} style={styles.processStep} className="process-step">
              <div style={styles.processStepNumber} className="process-step-number">{step.step}</div>
              <h3 style={styles.processStepTitle} className="process-step-title">{step.title}</h3>
              <p style={{color: "#2D5A74", lineHeight: "1.6", fontSize: "0.9rem"}}>{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Case Studies - Blue Theme */}
      <section style={{...styles.section, backgroundColor: "#DDE8ED"}} className="section">
        <h2 style={styles.sectionTitle} className="section-title">Successful Case Studies</h2>
        <div style={styles.sectionTitleAfter} className="section-title-after"></div>
        <p style={styles.sectionSubtitle} className="section-subtitle">
          Real-world examples of industrial solar installations and their impact
        </p>

        <div style={styles.caseStudiesGrid} className="case-studies-grid">
          {caseStudies.map((study, index) => (
            <div key={index} style={styles.caseStudyCard} className="case-study-card">
              <h3 style={styles.caseStudyCompany} className="case-study-company">{study.company}</h3>
              <div style={styles.caseStudyDetail} className="case-study-detail">
                <span style={styles.caseStudyLabel} className="case-study-label">Location:</span>
                <span style={styles.caseStudyValue} className="case-study-value">{study.location}</span>
              </div>
              <div style={styles.caseStudyDetail} className="case-study-detail">
                <span style={styles.caseStudyLabel} className="case-study-label">Capacity:</span>
                <span style={styles.caseStudyValue} className="case-study-value">{study.capacity}</span>
              </div>
              <div style={styles.caseStudyDetail} className="case-study-detail">
                <span style={styles.caseStudyLabel} className="case-study-label">Annual Savings:</span>
                <span style={styles.caseStudyValue} className="case-study-value">{study.savings}</span>
              </div>
              <div style={{...styles.caseStudyDetail, borderBottom: "none"}} className="case-study-detail">
                <span style={styles.caseStudyLabel} className="case-study-label">Details:</span>
                <span style={{...styles.caseStudyValue, fontWeight: "normal", fontSize: "0.9rem"}}>{study.details}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section - Blue Theme */}
      <section style={styles.ctaSection} className="cta-section">
        <h2 style={styles.ctaTitle} className="cta-title">Ready to Power Your Industry with Solar?</h2>
        <p style={styles.ctaSubtitle} className="cta-subtitle">
          Get a free feasibility study and customized solar proposal for your industrial facility. 
          Our experts will analyze your energy consumption and design the optimal solar solution.
        </p>
        <button 
          style={{
            ...styles.ctaButton,
            ...(hoverStates.ctaButton ? styles.ctaButtonHover : {})
          }}
          className="cta-button"
          onMouseEnter={() => handleMouseEnter('ctaButton')}
          onMouseLeave={() => handleMouseLeave('ctaButton')}
        >
          Request Free Consultation
        </button>
      </section>

      {/* Contact Form - Blue Theme */}
      <section style={styles.section} className="section">
        <h2 style={styles.sectionTitle} className="section-title">Get in Touch</h2>
        <div style={styles.sectionTitleAfter} className="section-title-after"></div>
        <p style={styles.sectionSubtitle} className="section-subtitle">
          Tell us about your industrial solar requirements
        </p>

        <div style={styles.contactForm} className="contact-form">
          <h3 style={styles.formTitle} className="form-title">Request a Quote</h3>
          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup} className="form-group">
              <label style={styles.formLabel} className="form-label">Full Name *</label>
              <input 
                type="text" 
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                style={styles.formInput}
                className="form-input"
                required
                onFocus={(e) => e.target.style.borderColor = '#447793'}
                onBlur={(e) => e.target.style.borderColor = '#DDE8ED'}
              />
            </div>
            
            <div style={styles.formGroup} className="form-group">
              <label style={styles.formLabel} className="form-label">Company Name *</label>
              <input 
                type="text" 
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                style={styles.formInput}
                className="form-input"
                required
                onFocus={(e) => e.target.style.borderColor = '#447793'}
                onBlur={(e) => e.target.style.borderColor = '#DDE8ED'}
              />
            </div>
            
            <div style={{display: "flex", gap: "20px", marginBottom: "25px", flexWrap: "wrap"}} className="form-input-group">
              <div style={{flex: "1", minWidth: "250px"}}>
                <label style={styles.formLabel} className="form-label">Email *</label>
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  style={styles.formInput}
                  className="form-input"
                  required
                  onFocus={(e) => e.target.style.borderColor = '#447793'}
                  onBlur={(e) => e.target.style.borderColor = '#DDE8ED'}
                />
              </div>
              <div style={{flex: "1", minWidth: "250px"}}>
                <label style={styles.formLabel} className="form-label">Phone *</label>
                <input 
                  type="tel" 
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  style={styles.formInput}
                  className="form-input"
                  required
                  onFocus={(e) => e.target.style.borderColor = '#447793'}
                  onBlur={(e) => e.target.style.borderColor = '#DDE8ED'}
                />
              </div>
            </div>
            
            <div style={styles.formGroup} className="form-group">
              <label style={styles.formLabel} className="form-label">Estimated Solar Capacity Required</label>
              <select 
                name="capacity"
                value={formData.capacity}
                onChange={handleInputChange}
                style={styles.formInput}
                className="form-input"
                onFocus={(e) => e.target.style.borderColor = '#447793'}
                onBlur={(e) => e.target.style.borderColor = '#DDE8ED'}
              >
                <option value="">Select capacity</option>
                <option value="<100kW">Less than 100kW</option>
                <option value="100kW-1MW">100kW - 1MW</option>
                <option value="1MW-5MW">1MW - 5MW</option>
                <option value="5MW-10MW">5MW - 10MW</option>
                <option value=">10MW">More than 10MW</option>
              </select>
            </div>
            
            <div style={styles.formGroup} className="form-group">
              <label style={styles.formLabel} className="form-label">Message / Requirements</label>
              <textarea 
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                style={styles.formTextarea}
                className="form-textarea"
                placeholder="Tell us about your specific requirements..."
                onFocus={(e) => e.target.style.borderColor = '#447793'}
                onBlur={(e) => e.target.style.borderColor = '#DDE8ED'}
              />
            </div>
            
            <button 
              type="submit"
              style={{
                ...styles.submitButton,
                ...(hoverStates.submitButton ? styles.submitButtonHover : {})
              }}
              className="submit-button"
              onMouseEnter={() => handleMouseEnter('submitButton')}
              onMouseLeave={() => handleMouseLeave('submitButton')}
            >
              Submit Request
            </button>
          </form>
        </div>
      </section>

      {/* Chatbot Component - Blue Theme */}
      <div style={styles.chatbotContainer}>
        {/* Chat Toggle Button */}
        <button 
          style={{
            ...styles.chatToggleBtn,
            animation: hoverStates.chatbotButton ? "pulse 0.5s infinite" : "pulse 2s infinite"
          }}
          className="chat-toggle-btn"
          onClick={() => setIsChatOpen(!isChatOpen)}
          onMouseEnter={() => handleMouseEnter('chatbotButton')}
          onMouseLeave={() => handleMouseLeave('chatbotButton')}
        >
          <i className="fas fa-comments"></i>
        </button>

        {/* Chat Window */}
        <div style={styles.chatWindow} className="chat-window">
          {/* Chat Header */}
          <div style={styles.chatHeader} className="chat-header">
            <div style={styles.headerInfo} className="chat-header-info">
              <div style={styles.headerIcon} className="header-icon">
                <i className="fas fa-industry"></i>
              </div>
              <div>
                <h3 style={styles.chatHeaderTitle} className="chat-header-title">Blue Sky Energy</h3>
                <p style={styles.chatHeaderSubtitle} className="chat-header-subtitle">Industrial Solar Experts</p>
              </div>
            </div>
            <button 
              style={styles.closeChatBtn}
              className="close-chat-btn"
              onClick={() => setIsChatOpen(false)}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Quick Replies */}
          {quickReplies.length > 0 && (
            <div style={styles.quickReplies} className="quick-replies">
              {quickReplies.map((option, index) => (
                <button
                  key={index}
                  style={styles.replyBtn}
                  className="reply-btn"
                  onClick={() => handleQuickReply(option)}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#447793';
                    e.target.style.color = 'white';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'white';
                    e.target.style.color = '#123249';
                    e.target.style.transform = 'none';
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          )}

          {/* Chat Messages */}
          <div style={styles.chatMessages} className="chat-messages">
            {chatMessages.map((msg) => (
              <div 
                key={msg.id} 
                style={{
                  ...styles.message,
                  ...(msg.sender === 'bot' ? styles.botMessage : styles.userMessage)
                }}
                className="message"
              >
                {msg.text}
                <span style={styles.messageTime} className="message-time">{msg.time}</span>
              </div>
            ))}
          </div>

          {/* Chat Input */}
          <div style={styles.chatInputArea} className="chat-input-area">
            <div style={styles.inputWrapper} className="input-wrapper">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                style={styles.chatInput}
                className="chat-input"
                onFocus={(e) => e.target.style.borderColor = '#447793'}
                onBlur={(e) => e.target.style.borderColor = '#DDE8ED'}
              />
              <button 
                style={styles.sendBtn}
                className="send-btn"
                onClick={handleChatSend}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #2D5A74, #123249)';
                  e.target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #447793, #2D5A74)';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                <i className="fas fa-paper-plane"></i>
              </button>
            </div>
            <p style={styles.footerText} className="footer-text">Powered by Blue Sky Energy</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Industrial;